import SunCalc from 'suncalc';
import { moonTimesForLocalDay, type MoonTimes } from './moonTimes';
import { moonPosition } from './moonPosition';
import { getSunTimes, type SunTimes } from './sunPosition';
import { getTimezoneFromCoordinates, toNearestMinute } from './timeFormatting';

export interface MoonPositionData {
  altitude: number;
  azimuth: number;
  azimuthDirection: string;
}

export interface MoonDayData {
  date: number;
  fullDate: string;
  dateObj: Date;
  moonrise: string;
  moonset: string;
  moonriseAzimuth: string;
  moonsetAzimuth: string;
  maxAltitude: number;
  maxAltitudeTime: string;
  moonPhase: number;
  moonPhaseEmoji: string;
  moonPhaseName: string;
  illumination: number;
  isFullMoon: boolean;
  isSupermoon: boolean;
  goldenHourOverlap: boolean;
  blueHourOverlap: boolean;
  twilightNote: string;
}

export interface FullMoonData extends MoonDayData {
  rating: number;
}

export function getAzimuthDirection(azimuthDegrees: number): string {
  // Convert radians to degrees if needed, normalize to 0-360
  let degrees = azimuthDegrees;
  if (degrees < 0) degrees += 360;
  
  const directions = [
    { min: 337.5, max: 360, dir: 'N' },
    { min: 0, max: 22.5, dir: 'N' },
    { min: 22.5, max: 67.5, dir: 'NE' },
    { min: 67.5, max: 112.5, dir: 'E' },
    { min: 112.5, max: 157.5, dir: 'SE' },
    { min: 157.5, max: 202.5, dir: 'S' },
    { min: 202.5, max: 247.5, dir: 'SW' },
    { min: 247.5, max: 292.5, dir: 'W' },
    { min: 292.5, max: 337.5, dir: 'NW' },
  ];
  
  for (const d of directions) {
    if (degrees >= d.min && degrees < d.max) {
      return d.dir;
    }
  }
  return 'N';
}

export function formatAzimuth(azimuthRadians: number): string {
  // SunCalc returns azimuth in radians, measured from south (-PI to PI)
  // Convert to degrees from north (0-360)
  let degrees = (azimuthRadians * 180 / Math.PI) + 180;
  if (degrees >= 360) degrees -= 360;
  if (degrees < 0) degrees += 360;
  
  const direction = getAzimuthDirection(degrees);
  return `${direction} ${Math.round(degrees)}°`;
}

export function getMoonPhaseEmoji(illumination: number, phase?: number): string {
  const percent = Math.round(illumination * 100);
  
  // Phase: 0 = new moon, 0.5 = full moon, 1 = new moon again
  // Before 0.5 = waxing (growing), After 0.5 = waning (shrinking)
  const isWaning = phase !== undefined && phase > 0.5;
  
  if (percent <= 5) return '🌑';      // New Moon (0-5%)
  else if (percent <= 40) return isWaning ? '🌘' : '🌒'; // Crescent (6-40%)
  else if (percent <= 60) return isWaning ? '🌗' : '🌓'; // Quarter (41-60%)
  else if (percent <= 95) return isWaning ? '🌖' : '🌔'; // Gibbous (61-95%)
  else return '🌕';                   // Full Moon (96-100%)
}

export function getMoonPhaseName(phase: number): string {
  // phase is 0-1, where 0 and 1 are new moon, 0.5 is full moon
  if (phase < 0.03 || phase > 0.97) return 'New Moon';
  else if (phase < 0.22) return 'Waxing Crescent';
  else if (phase < 0.28) return 'First Quarter';
  else if (phase < 0.47) return 'Waxing Gibbous';
  else if (phase < 0.53) return 'Full Moon';
  else if (phase < 0.72) return 'Waning Gibbous';
  else if (phase < 0.78) return 'Last Quarter';
  else return 'Waning Crescent';
}

// A supermoon is a full moon near perigee (the moon's closest approach to Earth).
// We derive this from SunCalc's Earth–Moon distance rather than a hardcoded perigee
// table, so detection works for any year (the old table only covered 2024–2026 and
// silently failed for later years).
//
// Note: SunCalc uses a simplified lunar model whose distance bottoms out around
// 364,100 km (versus the true perigee of ~356,500 km). The threshold below is
// therefore calibrated to SunCalc's own range — 367,000 km corresponds to roughly
// the real-world ~360,000 km supermoon cutoff and flags the closest full moons each
// year. Callers gate this with a full-moon check (see getMoonDayData).
const SUPERMOON_DISTANCE_KM = 367000;

export function isSupermoon(date: Date): boolean {
  // The Earth–Moon distance is geocentric, so observer coordinates are irrelevant;
  // sample at local noon for a stable, deterministic daily value.
  const noon = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
  const { distance } = SunCalc.getMoonPosition(noon, 0, 0);
  return distance <= SUPERMOON_DISTANCE_KM;
}

function formatTime(date: Date | null): string {
  if (!date || isNaN(date.getTime())) return '--:--';
  return toNearestMinute(date).toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit', 
    hour12: true 
  });
}

export function calculateMoonDataForDay(
  date: Date,
  latitude: number,
  longitude: number
): MoonDayData {
  // Use UTC noon to avoid timezone issues
  const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0));
  
  // Get moon times
  // The location's own day, not the browser's (ERI-2380).
  const moonTimes = moonTimesForLocalDay(
    date.getFullYear(), date.getMonth(), date.getDate(), latitude, longitude,
    getTimezoneFromCoordinates(latitude, longitude),
  );
  const sunTimes = getSunTimes(utcDate, latitude, longitude);
  
  // Get moon illumination
  const moonIllum = SunCalc.getMoonIllumination(utcDate);
  
  // Calculate moonrise/moonset azimuths
  let moonriseAzimuth = '--';
  let moonsetAzimuth = '--';
  
  if (moonTimes.rise) {
    const risePos = moonPosition(moonTimes.rise, latitude, longitude);
    moonriseAzimuth = formatAzimuth(risePos.azimuth);
  }
  
  if (moonTimes.set) {
    const setPos = moonPosition(moonTimes.set, latitude, longitude);
    moonsetAzimuth = formatAzimuth(setPos.azimuth);
  }
  
  // Calculate max altitude throughout the night
  let maxAltitude = -90;
  let maxAltitudeTime = utcDate;
  
  // Sample every 30 minutes to find max altitude
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += 30) {
      const checkTime = new Date(utcDate);
      checkTime.setHours(hour, min, 0, 0);
      const pos = moonPosition(checkTime, latitude, longitude);
      const altDegrees = pos.altitude * 180 / Math.PI;
      if (altDegrees > maxAltitude) {
        maxAltitude = altDegrees;
        maxAltitudeTime = new Date(checkTime);
      }
    }
  }
  
  // Check golden hour overlap (around sunrise/sunset)
  const goldenHourOverlap = checkMoonDuringGoldenHour(utcDate, latitude, longitude, sunTimes, moonTimes);
  const blueHourOverlap = checkMoonDuringBlueHour(utcDate, latitude, longitude, sunTimes, moonTimes);
  
  // Generate twilight note
  let twilightNote = '';
  if (goldenHourOverlap && blueHourOverlap) {
    twilightNote = 'Visible during golden & blue hour';
  } else if (goldenHourOverlap) {
    twilightNote = 'Visible during golden hour';
  } else if (blueHourOverlap) {
    twilightNote = 'Visible during blue hour';
  }
  
  const isFullMoonDay = moonIllum.phase >= 0.47 && moonIllum.phase <= 0.53;
  const isSupermoonDay = isFullMoonDay && isSupermoon(date);
  
  return {
    date: date.getDate(),
    fullDate: date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    }),
    dateObj: date,
    moonrise: formatTime(moonTimes.rise || null),
    moonset: formatTime(moonTimes.set || null),
    moonriseAzimuth,
    moonsetAzimuth,
    maxAltitude: Math.round(maxAltitude),
    maxAltitudeTime: formatTime(maxAltitudeTime),
    moonPhase: moonIllum.phase,
    moonPhaseEmoji: getMoonPhaseEmoji(moonIllum.fraction, moonIllum.phase),
    moonPhaseName: getMoonPhaseName(moonIllum.phase),
    illumination: Math.round(moonIllum.fraction * 100),
    isFullMoon: isFullMoonDay,
    isSupermoon: isSupermoonDay,
    goldenHourOverlap,
    blueHourOverlap,
    twilightNote
  };
}

function checkMoonDuringGoldenHour(
  date: Date,
  latitude: number,
  longitude: number,
  sunTimes: SunTimes,
  moonTimes: MoonTimes
): boolean {
  // Golden hour is roughly 1 hour after sunrise and 1 hour before sunset
  if (!sunTimes.sunrise || !sunTimes.sunset) return false;
  
  const goldenMorningEnd = new Date(sunTimes.sunrise.getTime() + 60 * 60 * 1000);
  const goldenEveningStart = new Date(sunTimes.sunset.getTime() - 60 * 60 * 1000);
  
  // Check if moon is above horizon during golden hour
  const morningPos = moonPosition(goldenMorningEnd, latitude, longitude);
  const eveningPos = moonPosition(goldenEveningStart, latitude, longitude);
  
  return morningPos.altitude > 0 || eveningPos.altitude > 0;
}

function checkMoonDuringBlueHour(
  date: Date,
  latitude: number,
  longitude: number,
  sunTimes: SunTimes,
  moonTimes: MoonTimes
): boolean {
  // Blue hour is civil twilight (sun 0° to -6° below horizon)
  if (!sunTimes.dawn || !sunTimes.dusk) return false;
  
  const blueHourMorning = sunTimes.dawn;
  const blueHourEvening = sunTimes.dusk;
  
  const morningPos = moonPosition(blueHourMorning, latitude, longitude);
  const eveningPos = moonPosition(blueHourEvening, latitude, longitude);
  
  return morningPos.altitude > 0 || eveningPos.altitude > 0;
}

export function generateMonthlyMoonData(
  year: number,
  month: number,
  latitude: number,
  longitude: number
): MoonDayData[] {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthData: MoonDayData[] = [];
  
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    monthData.push(calculateMoonDataForDay(date, latitude, longitude));
  }
  
  return monthData;
}

export function getBestFullMoons(monthData: MoonDayData[]): FullMoonData[] {
  // Filter for full moons and near-full moons (illumination > 90%)
  const fullMoons = monthData
    .filter(day => day.illumination >= 90)
    .map(day => {
      // Rating based on: supermoon (bonus), max altitude, twilight overlap
      let rating = 5; // Base rating
      
      if (day.isSupermoon) rating += 3;
      if (day.goldenHourOverlap) rating += 1;
      if (day.blueHourOverlap) rating += 1;
      if (day.maxAltitude > 60) rating += 1;
      else if (day.maxAltitude > 40) rating += 0.5;
      
      return {
        ...day,
        rating: Math.min(10, Math.round(rating))
      };
    })
    .sort((a, b) => b.rating - a.rating);
  
  return fullMoons;
}
